import { useState } from "react";
import { Clock, AlertTriangle, CalendarDays } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useNotifications } from "@/hooks/useDashboard";
import { cn } from "@/lib/utils";
import { NotificationComplaint } from "@/api/dashboard.api";

const TABS = [
  { key: "pending" as const, label: "Pending", icon: Clock },
  { key: "overdue" as const, label: "Overdue", icon: AlertTriangle },
  { key: "today" as const, label: "Today", icon: CalendarDays },
];

function NotificationRow({ item }: { item: NotificationComplaint }) {
  return (
    <div className="flex items-center justify-between border-b border-border py-2.5 last:border-0">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium font-data">{item.complaintNumber}</p>
        <p className="truncate text-xs text-muted-foreground">
          {item.customer.fullName} · {item.productName}
        </p>
      </div>
      <StatusBadge status={item.status} />
    </div>
  );
}

export function NotificationsPanel() {
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("pending");
  const { data, isLoading } = useNotifications();

  const items = data?.[tab] ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>Jobs that need attention</CardDescription>
        <div className="flex gap-1 pt-2">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
                tab === key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
              {data && data[key].length > 0 && (
                <span className="rounded-full bg-background/20 px-1.5 text-[10px]">
                  {data[key].length}
                </span>
              )}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Nothing here. All caught up.</p>
        ) : (
          <div>
            {items.map((item) => (
              <NotificationRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
